import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../../css/GlobalTextStyles.css";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import { useWindowResize } from "../../hooks/useWindowResize";
import { PlanTypeV4 } from "@markit/common.types";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import SubscriptionPlanPrice from "./SubscriptionPlanPrice";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import DropdownArrowIcon from "../DropdownsAndTabs/DropdownArrowIcon";
import TextHoverButton from "../Buttons/TextHoverButton";
import { MARKIT_DOCS } from "@markit/common.utils";

type FeatureValue = boolean | string;

interface ComparisonRow {
  feature: string;
  values: [FeatureValue, FeatureValue, FeatureValue, FeatureValue];
}

interface ComparisonSection {
  title: string;
  rows: ComparisonRow[];
}

const SUBSCRIPTION_PLANS = [
  PlanTypeV4.FREE,
  PlanTypeV4.ESSENTIAL,
  PlanTypeV4.PRO,
  PlanTypeV4.BUSINESS,
];

const FEATURE_COL_MAX = 280;
const PLAN_COL_MAX = 268;

const getTableMaxWidth = (planCount: number) =>
  FEATURE_COL_MAX + planCount * PLAN_COL_MAX;
const CELL_PADDING_INLINE = 20;
const CELL_PADDING_BLOCK = 14;
const HEADER_PADDING_BLOCK = 20;
const COMPARISON_SECTIONS: ComparisonSection[] = [
  {
    title: "TEXTING",
    rows: [
      {
        feature: "Free credits / mo",
        values: ["500 (capped)", "0", "1,000", "10,000"],
      },
      { feature: "Cost per credit", values: [false, "1.5¢", "1.25¢", "1.1¢"] },
      { feature: "Dedicated phone number", values: [false, true, true, true] },
      { feature: "Branded contact card", values: [false, true, true, true] },
      {
        feature: "Custom local area code number",
        values: [false, false, false, true],
      },
      {
        feature: "Scheduled & drip campaigns",
        values: [true, true, true, true],
      },
      { feature: "Campaign analytics", values: [true, true, true, true] },
      { feature: "Two-way messaging", values: [false, true, true, true] },
      {
        feature: "Inbox segmentation & filters",
        values: [false, true, true, true],
      },
      {
        feature: "AI-assistive messaging",
        values: [false, false, true, true],
      },
      { feature: "MMS / images & media", values: [false, false, true, true] },
      { feature: "Faster send times", values: [false, false, false, true] },
    ],
  },
  {
    title: "MONETIZATION",
    rows: [
      {
        feature: "Platform fee on paid subs & products*",
        values: ["10%", "10%", "0%", "0%"],
      },
      {
        feature: "Event ticketing fees*",
        values: [
          "4.88% + $1.49",
          "4.88% + $1.49",
          "2.88% + $1.29",
          "1.88% + $0.99",
        ],
      },
      {
        feature: "Digital products (video, audio, PDF)",
        values: [true, true, true, true],
      },
      { feature: "Custom newsletter/blog", values: [true, true, true, true] },
      {
        feature: "Ticketed events and webinars",
        values: [true, true, true, true],
      },
      { feature: "Paid text memberships", values: [false, true, true, true] },
      {
        feature: "Apple Pay, Google Pay, Cashapp, etc",
        values: [true, true, true, true],
      },
      {
        feature: "Affiliate & promoter links",
        values: [false, true, true, true],
      },
    ],
  },
  {
    title: "GROWTH & INTEGRATIONS",
    rows: [
      { feature: "Link-in-bio", values: [true, true, true, true] },
      {
        feature: "Surveys & data capture forms",
        values: [true, true, true, true],
      },
      {
        feature: "Instagram automations",
        values: [false, true, true, true],
      },
      {
        feature: "Full integration access",
        values: [false, false, true, true],
      },
      { feature: "API & webhooks", values: [false, false, true, true] },
      { feature: "AI tools (MCP)", values: [false, true, true, true] },
    ],
  },
  {
    title: "AUDIENCE",
    rows: [
      {
        feature: "Audience lists",
        values: ["1 list", "Unlimited", "Unlimited", "Unlimited"],
      },
      {
        feature: "Custom contact data fields",
        values: [true, true, true, true],
      },
      { feature: "Manual add contact", values: [true, true, true, true] },
      {
        feature: '"Text my number" to sign up',
        values: [false, true, true, true],
      },
      { feature: "Import CSV", values: [false, false, true, true] },
    ],
  },
  {
    title: "TEAM & SUPPORT",
    rows: [
      {
        feature: "Team members (seats)",
        values: ["1 seat", "1 seat", "3 seats", "Unlimited seats"],
      },
      {
        feature: "Human support",
        values: [false, true, "Priority", "Dedicated rep"],
      },
      {
        feature: "White glove onboarding",
        values: [false, false, false, true],
      },
      {
        feature: "Community & weekly updates",
        values: [false, true, true, true],
      },
    ],
  },
];

const PAYMENT_DISCLAIMER =
  "*All plans include standard payment processing of 3.12% + $0.30 per transaction.";

const ComparisonDisclaimer = () => (
  <div
    className="AlignedRow"
    style={{ gap: 4, justifyContent: "center", flexWrap: "wrap" }}
  >
    <span
      className="bodyRegular"
      style={{ color: Colors.WHITE, paddingInline: 4, textAlign: "center" }}
    >
      {PAYMENT_DISCLAIMER}
    </span>
    <TextHoverButton
      text="Learn more"
      onPress={() => {
        window.open(MARKIT_DOCS + "/fees");
      }}
      icon={
        <Icon
          icon="ion:chevron-forward"
          height={12}
          style={{ color: Colors.BLUE5 }}
        />
      }
      iconPosition="right"
      textStyles={{ color: Colors.BLUE5, fontSize: 14 }}
    />
  </div>
);

const ComparisonSectionTitle = ({
  title,
  fullWidth,
}: {
  title: string;
  fullWidth?: boolean;
}) => (
  <div
    style={{
      ...(fullWidth ? { gridColumn: "1 / -1" } : {}),
      backgroundColor: Colors.GRAY10,
      padding: `10px ${CELL_PADDING_INLINE}px`,
      borderTop: `1px solid ${Colors.GRAY8}`,
    }}
  >
    <span
      className="smallBodySubtextMedium"
      style={{ textTransform: "uppercase" }}
    >
      {title}
    </span>
  </div>
);

const FeatureValueIndicator = ({
  value,
  iconSize = 18,
  textAlign,
}: {
  value: FeatureValue;
  iconSize?: number;
  textAlign?: React.CSSProperties["textAlign"];
}) => {
  if (typeof value === "boolean") {
    return value ? (
      <Icon
        icon="ion:checkmark-circle"
        height={iconSize}
        style={{ color: Colors.WHITE, flexShrink: 0 }}
      />
    ) : (
      <Icon
        icon="ion:close-outline"
        height={iconSize}
        style={{ color: Colors.GRAY3, flexShrink: 0 }}
      />
    );
  }

  const styles = {
    text: {
      color: Colors.WHITE,
      overflowWrap: "break-word",
      wordBreak: "break-word",
      ...(textAlign
        ? {
            flexShrink: 0,
            textAlign,
            height: iconSize,
            lineHeight: `${iconSize}px`,
            display: "inline-flex",
            alignItems: "center",
          }
        : { minWidth: 0, width: "100%" }),
    } as React.CSSProperties,
  };

  return (
    <span className="bodyMedium" style={styles.text}>
      {value}
    </span>
  );
};

const PlanComparisonTableMobileFn = () => {
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const selectedPlan = SUBSCRIPTION_PLANS[selectedPlanIndex];

  const planDropdownItems: DropdownMenuItem[] = useMemo(
    () =>
      SUBSCRIPTION_PLANS.map((plan, index) => ({
        title: plan.name,
        key: plan.name,
        onPress: () => setSelectedPlanIndex(index),
      })),
    []
  );

  return (
    <div className="ColumnNormal" style={{ gap: 20, width: "100%" }}>
      <div
        style={{
          border: `1px solid ${Colors.GRAY8}`,
          borderRadius: 20,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: `${HEADER_PADDING_BLOCK}px ${CELL_PADDING_INLINE}px`,
            borderBottom: `1px solid ${Colors.GRAY8}`,
          }}
        >
          <span style={{ fontSize: 20, fontWeight: 700, color: Colors.WHITE }}>
            Features
          </span>
          <DropdownMenu
            dropdownItems={planDropdownItems}
            trigger={["click"]}
            placement="bottomRight"
            dark
            altWidth={120}
            topPadding={4}
            onOpenChange={setDropdownVisible}
          >
            <div
              className="AlignedRow"
              style={{
                gap: 8,
                backgroundColor: Colors.GRAY10,
                border: `1px solid ${Colors.GRAY8}`,
                borderRadius: 8,
                padding: "10px 14px",
              }}
            >
              <span className="bodyMedium" style={{ color: Colors.WHITE }}>
                {selectedPlan.name}
              </span>
              <DropdownArrowIcon
                isOpen={dropdownVisible}
                color={Colors.WHITE}
              />
            </div>
          </DropdownMenu>
        </div>

        {COMPARISON_SECTIONS.map((section) => (
          <React.Fragment key={section.title}>
            <ComparisonSectionTitle title={section.title} />
            {section.rows.map((row) => (
              <div
                key={row.feature}
                className="AlignedRowSpaced"
                style={{
                  gap: 12,
                  padding: `${CELL_PADDING_BLOCK}px ${CELL_PADDING_INLINE}px`,
                  borderTop: `1px solid ${Colors.GRAY8}`,
                }}
              >
                <span
                  className="bodyMedium"
                  style={{
                    color: Colors.WHITE,
                    flex: 1,
                    minWidth: 0,
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  {row.feature}
                </span>
                <FeatureValueIndicator
                  value={row.values[selectedPlanIndex]}
                  iconSize={20}
                  textAlign="right"
                />
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <ComparisonDisclaimer />
    </div>
  );
};

const PlanComparisonTableMobile = memo(PlanComparisonTableMobileFn);

interface TableLayout {
  tableWidth: number;
  gridTemplateColumns: string;
}

const getCellStyle = (
  colIndex: number,
  options?: { rowBackground?: string; isHeader?: boolean }
): React.CSSProperties => {
  const isFeatureCol = colIndex === 0;
  const paddingBlock = options?.isHeader
    ? HEADER_PADDING_BLOCK
    : CELL_PADDING_BLOCK;

  return {
    padding: `${paddingBlock}px ${CELL_PADDING_INLINE}px`,
    borderLeft: isFeatureCol ? undefined : `1px solid ${Colors.BLACK1}`,
    borderTop: options?.isHeader ? undefined : `1px solid ${Colors.BLACK1}`,
    display: "flex",
    alignItems: "center",
    justifyContent: isFeatureCol ? "flex-start" : "center",
    minWidth: 0,
    overflow: "hidden",
    backgroundColor: "transparent",
  };
};

const ComparisonCellContent = ({
  children,
  centered,
}: {
  children: React.ReactNode;
  centered?: boolean;
}) => (
  <div
    style={{
      minWidth: 0,
      width: "100%",
      overflow: "hidden",
      overflowWrap: "break-word",
      wordBreak: "break-word",
      ...(centered && {
        display: "flex",
        justifyContent: "center",
        textAlign: "center",
      }),
    }}
  >
    {children}
  </div>
);

const PlanComparisonTableDesktopFn = ({
  tableLayout,
}: {
  tableLayout: TableLayout;
}) => {
  const { tableWidth, gridTemplateColumns } = tableLayout;

  const renderCell = useCallback(
    (value: FeatureValue, colIndex: number, rowBackground?: string) => {
      const isFeatureCol = colIndex === 0;
      return (
        <div style={getCellStyle(colIndex, { rowBackground })}>
          <ComparisonCellContent centered={!isFeatureCol}>
            <FeatureValueIndicator value={value} />
          </ComparisonCellContent>
        </div>
      );
    },
    []
  );

  const headerPadding = `${HEADER_PADDING_BLOCK}px ${CELL_PADDING_INLINE}px`;

  return (
    <div
      className="AlignedColumn"
      style={{
        width: tableWidth,
        maxWidth: getTableMaxWidth(SUBSCRIPTION_PLANS.length),
        margin: "0 auto",
        gap: 24,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns,
          width: "100%",
          border: `1px solid ${Colors.GRAY8}`,
          borderRadius: 20,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            ...getCellStyle(0, { isHeader: true }),
            padding: headerPadding,
            borderBottom: `1px solid ${Colors.GRAY8}`,
          }}
        >
          <span
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: Colors.WHITE,
              lineHeight: 1,
            }}
          >
            Features
          </span>
        </div>
        {SUBSCRIPTION_PLANS.map((subscriptionPlan, colIndex) => (
          <div
            key={subscriptionPlan.name}
            className="ColumnNormal"
            style={{
              ...getCellStyle(colIndex + 1, { isHeader: true }),
              padding: headerPadding,
              borderBottom: `1px solid ${Colors.GRAY8}`,
              gap: 4,
            }}
          >
            <span
              className="bodyMedium"
              style={{
                color: Colors.WHITE,
                minWidth: 0,
                width: "100%",
                overflowWrap: "break-word",
                wordBreak: "break-word",
                textAlign: "center",
              }}
            >
              {subscriptionPlan.name}
            </span>
            <SubscriptionPlanPrice
              priceInCents={subscriptionPlan.price}
              homePage
            />
          </div>
        ))}
        {COMPARISON_SECTIONS.map((section) => (
          <React.Fragment key={section.title}>
            <ComparisonSectionTitle title={section.title} fullWidth />
            {section.rows.map((row) => (
              <React.Fragment key={row.feature}>
                {renderCell(row.feature, 0)}
                {row.values.map((val, colIndex) =>
                  renderCell(val, colIndex + 1)
                )}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </div>
      <ComparisonDisclaimer />
    </div>
  );
};

const PlanComparisonTableDesktop = memo(PlanComparisonTableDesktopFn);

const PlanComparisonTableFn = () => {
  const windowWidth = useWindowResize();
  const containerRef = useRef<HTMLDivElement>(null);
  const tableMaxWidth = getTableMaxWidth(SUBSCRIPTION_PLANS.length);
  const [containerWidth, setContainerWidth] = useState(tableMaxWidth);

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth, true),
    [windowWidth]
  );

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateWidth = () => {
      setContainerWidth(element.clientWidth);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const tableLayout = useMemo((): TableLayout => {
    const columnCount = 1 + SUBSCRIPTION_PLANS.length;
    const tableWidth = Math.min(containerWidth, tableMaxWidth);
    const equalShrink = (tableMaxWidth - tableWidth) / columnCount;
    const featureCol = FEATURE_COL_MAX - equalShrink;
    const planCol = PLAN_COL_MAX - equalShrink;

    return {
      tableWidth,
      gridTemplateColumns: `${featureCol}px repeat(${SUBSCRIPTION_PLANS.length}, ${planCol}px)`,
    };
  }, [containerWidth, tableMaxWidth]);

  if (!isDesktopSize) {
    return (
      <div ref={containerRef} style={{ width: "100%" }}>
        <PlanComparisonTableMobile />
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <PlanComparisonTableDesktop tableLayout={tableLayout} />
    </div>
  );
};

const PlanComparisonTable = memo(PlanComparisonTableFn);

export default PlanComparisonTable;
